import "./room-cards";
import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { ChatMessage } from "../widget/state";
import { loadedPhotos, renderReply } from "../render/markdown";

@customElement("cg-message-bubble")
export class CGMessageBubble extends LitElement {
  static override styles = css`
    :host {
      display: block;
      max-width: 100%;
    }

    .bubble {
      max-width: 82%;
      padding: 10px 14px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.45;
      word-wrap: break-word;
      white-space: normal;
      animation: fadeInUp 120ms ease-out;
    }

    /* User bubble */
    :host([variant="USER"]) .bubble {
      background: var(--cg-bubble-user-bg, var(--cg-chat-bubble));
      color: var(--cg-bubble-user-text, var(--cg-on-primary));
      border-radius: 20px;
      font-weight: 500;
    }

    /* agent bubble */
    :host([variant="AGENT"]) .bubble {
      background: var(--cg-bubble-agent-bg, var(--cg-surface));
      color: var(--cg-bubble-agent-text, var(--cg-text));
      border: 1px solid var(--cg-border);
      border-radius: 12px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      font-weight: 500;
    }

    /* Streaming pulse */
    :host([streaming]) .bubble {
      opacity: 0.9;
    }

    /* ===========================
   Markdown Typography
=========================== */

    .bubble > * {
      line-height: 1.5;
      white-space: normal;
    }

    /* Headings */

    .bubble h1,
    .bubble h2,
    .bubble h3,
    .bubble h4 {
      font-weight: 600;
      line-height: 1.25;
      margin: 0.6em 0 0.4em;
    }

    .bubble h1 {
      font-size: 1.15em;
    }
    .bubble h2 {
      font-size: 1.08em;
    }
    .bubble h3 {
      font-size: 1.02em;
    }
    .bubble h4 {
      font-size: 1em;
    }

    /* Paragraphs */

    .bubble p {
      margin: 0.45em 0;
    }

    .bubble p:first-child {
      margin-top: 0;
    }

    .bubble p:last-child {
      margin-bottom: 0;
    }

    /* Lists */

    .bubble ul,
    .bubble ol {
      padding-left: 1.1em;
      margin: 0.4em 0;
    }

    .bubble li {
      margin: 0.2em 0;
    }

    .bubble li::marker {
      color: var(--cg-muted);
    }

    /* Blockquotes */

    .bubble blockquote {
      margin: 0.5em 0;
      padding: 0.4em 0.6em;
      border-left: 3px solid var(--cg-border);
      background: color-mix(in srgb, var(--cg-bg) 92%, transparent);
      border-radius: 6px;
      color: var(--cg-muted);
      font-style: italic;
    }

    /* Inline code */

    .bubble code {
      font-family: var(--cg-font);
      background: color-mix(in srgb, var(--cg-bg) 88%, black);
      padding: 2px 5px;
      border-radius: 6px;
      font-size: 12px;
    }

    /* Code blocks */

    .bubble pre {
      background: color-mix(in srgb, var(--cg-bg) 90%, black);
      padding: 10px 12px;
      border-radius: 10px;
      overflow-x: auto;
      font-size: 12px;
      line-height: 1.4;
      margin: 0.5em 0;
    }

    .bubble pre code {
      background: transparent;
      padding: 0;
      font-size: inherit;
    }

    /* Tables */

    .bubble table {
      width: 100%;
      border-collapse: collapse;
      margin: 0.5em 0;
      font-size: 12.5px;
    }

    .bubble th,
    .bubble td {
      border: 1px solid var(--cg-border);
      padding: 6px 8px;
      text-align: left;
    }

    .bubble th {
      background: color-mix(in srgb, var(--cg-bg) 90%, black);
      font-weight: 600;
    }

    /* Links */

    .bubble a {
      color: var(--cg-bubble-agent-text);
      text-decoration: none;
      font-weight: 500;
    }

    .bubble a:hover {
      text-decoration: underline;
    }

    /* Horizontal rule */

    .bubble hr {
      border: none;
      border-top: 1px solid var(--cg-border);
      margin: 0.6em 0;
    }

    /* Images */

    .bubble img {
      display: block;
      height: auto;
      max-height: 288px;
      object-fit: contain;
      max-width: 100%;
      border-radius: 8px;
      margin: 0.4em 0;
    }

    /* A photo still arriving or loading: a shimmering box instead of its link. */
    .photo-skeleton,
    .bubble img:not(.loaded) {
      display: block;
      width: 240px;
      max-width: 100%;
      height: 160px;
      border-radius: 8px;
      margin: 0.4em 0;
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.06) 25%, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0.06) 75%);
      background-size: 200% 100%;
      animation: photoShimmer 1.2s ease-in-out infinite;
    }
    .bubble img:not(.loaded) {
      color: transparent;
    }

    /* Rooms and extras: one card each, side by side, swipe to see more. */
    .cg-carousel {
      display: flex;
      gap: 8px;
      margin: 0.4em -2px;
      padding: 2px 2px 6px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
    }
    .cg-card {
      flex: 0 0 78%;
      max-width: 240px;
      scroll-snap-align: start;
      border: 1px solid var(--cg-border);
      border-radius: 12px;
      padding: 8px 10px;
      background: var(--cg-surface);
      font-size: 13px;
      line-height: 1.4;
    }
    .cg-card > :first-child {
      margin-top: 0;
    }
    .cg-card p {
      margin: 0.2em 0;
    }
    .cg-card img,
    .cg-card img:not(.loaded) {
      width: 100%;
      height: 130px;
      object-fit: cover;
      margin: 0 0 6px;
    }

    @keyframes photoShimmer {
      from {
        background-position: 200% 0;
      }
      to {
        background-position: -200% 0;
      }
    }

    @keyframes fadeInUp {
      from {
        transform: translateY(2px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `;

  @property({ type: Boolean }) roomSelectionDisabled = false;
  @property({ attribute: false }) message?: ChatMessage;
  @property({ type: String, reflect: true }) variant:
    | "USER"
    | "AGENT"
    | "SYSTEM" = "AGENT";

  override render() {
    if (!this.message) return nothing;

    const content = this.message.content ?? "";

    // User messages render as text (Lit safely escapes)
    if (this.variant === "USER") {
      return html`<div class="bubble">${content}</div>`;
    }

    // Agent messages render sanitized markdown HTML, part by part: while a
    // reply streams in, parts that haven't changed keep their DOM (and
    // photos), so only the part still arriving is redrawn. Runs of cards
    // (rooms, extras) are shown side by side in a slider.
    const parts = renderReply(content);
    return html`<div class="bubble">${parts.map((part) =>
      part.kind === "html"
        ? html`<div class="part">${unsafeHTML(part.html)}</div>`
        : html`<div class="cg-carousel" role="list">${part.cards.map(
            (card) =>
              html`<div class="cg-card" role="listitem">${card.map(
                (block) => html`<div class="part">${unsafeHTML(block)}</div>`,
              )}</div>`,
          )}</div>`,
    )}
      ${this.message.roomCards ? html`<cg-room-cards .rooms=${this.message.roomCards} .disabled=${this.roomSelectionDisabled}></cg-room-cards>` : nothing}
    </div>`;
  }

  /** Photos keep their placeholder until they have loaded (or failed). */
  override updated() {
    for (const img of this.renderRoot.querySelectorAll<HTMLImageElement>(".bubble img:not(.loaded)")) {
      const done = () => {
        img.classList.add("loaded");
        loadedPhotos.add(img.getAttribute("src") || "");
      };
      if (img.complete && img.naturalWidth > 0) done();
      else {
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      }
    }
  }
}
